import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, RefreshCw, Mic, Check, Copy } from "lucide-react";

import { FlowDiagram } from "./FlowDiagram";

/**
 * What Matters Here — the unmissable strip directly under the Decision banner.
 * Surfaces the two deterministic sentences that deliver the entire thesis
 * (dominant cost + value floor) without forcing the PM to expand the Cost or
 * Risk cards. Includes a Copy button that puts a 3-line markdown snippet on
 * the clipboard — the format PMs actually paste into their own docs.
 */
const WhatMattersStrip = ({ decision, computed, modelLabel }) => {
  const [copied, setCopied] = useState(false);
  const dom = computed?.dominant_cost?.sentence;
  const floor = computed?.value_floor?.sentence;
  const callsEstimated = computed?.calls_estimated;
  if (!dom && !floor) return null;

  const snippet = [
    decision?.headline && `**${decision.headline}**`,
    floor,
    dom,
    modelLabel && `_(Analyzed with ${modelLabel} on SuperHumanly)_`,
  ]
    .filter(Boolean)
    .join("\n\n");

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch (_) {
      // Fallback: select-all in a hidden textarea
      const ta = document.createElement("textarea");
      ta.value = snippet;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    }
  };

  return (
    <div
      className="mt-4 border border-[#DDD9CF] bg-white rounded-md p-5 xl:p-6"
      data-testid="what-matters-strip"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#56534A]">
          What matters here
        </div>
        <button
          type="button"
          onClick={onCopy}
          className="text-xs text-[#56534A] hover:text-[#C4521E] transition-colors flex items-center gap-1.5"
          data-testid="copy-summary"
          aria-label="Copy summary to clipboard"
        >
          {copied ? (
            <>
              <Check size={13} strokeWidth={1.8} className="text-[#5A8A3A]" />
              <span className="text-[#5A8A3A]">Copied</span>
            </>
          ) : (
            <>
              <Copy size={13} strokeWidth={1.5} />
              <span>Copy these numbers</span>
            </>
          )}
        </button>
      </div>
      <div className="mt-3 space-y-3">
        {floor && (
          <p className="text-base text-[#1A1814] leading-relaxed" data-testid="strip-floor">
            {floor}
          </p>
        )}
        {dom && (
          <p className="text-sm text-[#56534A] leading-relaxed" data-testid="strip-dominant">
            {dom}
          </p>
        )}
        {callsEstimated && (
          <p className="text-[11px] text-[#56534A] italic leading-relaxed" data-testid="strip-estimated">
            Note: monthly volume wasn't explicit in your inputs — estimated at {computed?.calls_per_day} calls/day. Re-run with a sharper volume estimate for tighter numbers.
          </p>
        )}
      </div>
    </div>
  );
};

/** Collapsible card matching the screenshot — title in terracotta, headline below, chevron right. */
const Card = ({ title, headline, defaultOpen = false, testid, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div
      className="border border-[#DDD9CF] bg-white rounded-md"
      data-testid={testid}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-start justify-between p-5 text-left group"
      >
        <div className="flex-1 pr-4">
          <h3 className="font-serif-display text-xl text-[#C4521E] group-hover:opacity-80 transition-opacity">
            {title}
          </h3>
          {headline && (
            <p className="text-[#1A1814] text-sm mt-2 leading-relaxed">{headline}</p>
          )}
        </div>
        <ChevronDown
          size={16}
          strokeWidth={1.5}
          className={`text-[#56534A] mt-1 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 pt-1 border-t border-[#DDD9CF]">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const KVRow = ({ label, children }) => (
  <div className="py-3 border-b border-[#DDD9CF] last:border-b-0">
    <div className="text-[10px] uppercase tracking-[0.14em] text-[#56534A] mb-1">{label}</div>
    <div className="text-[#1A1814] text-sm leading-relaxed">{children}</div>
  </div>
);

const Pill = ({ tone, children }) => {
  const cls =
    tone === "High"
      ? "border-[#C4521E] text-[#C4521E]"
      : tone === "Medium"
      ? "border-[#56534A] text-[#56534A]"
      : tone === "Low"
      ? "border-[#DDD9CF] text-[#56534A]"
      : "border-[#DDD9CF] text-[#56534A]";
  return (
    <span className={`text-[10px] uppercase tracking-[0.16em] border px-2 py-0.5 ${cls}`}>
      {children}
    </span>
  );
};

const ValueBody = ({ data }) => (
  <div className="pt-2">
    <KVRow label="Who benefits">{data.who_benefits}</KVRow>
    <KVRow label="Measurable improvement">{data.measurable_improvement}</KVRow>
    <KVRow label="At current scale">{data.at_current_scale}</KVRow>
    <KVRow label="At 10× scale">{data.at_10x_scale}</KVRow>
    {data.does_not_solve && (
      <p className="text-xs italic text-[#56534A] pt-4 leading-relaxed">
        Does not solve: {data.does_not_solve}
      </p>
    )}
  </div>
);

const TYPE_COLORS = {
  API: { dot: "#C4521E", text: "#7A2E0C" },
  Cached: { dot: "#E8A930", text: "#7A5A12" },
  Free: { dot: "#9B9589", text: "#56534A" },
};

const CostBars = ({ current, tenx }) => {
  const max = Math.max(current || 0, tenx || 0, 1);
  const cw = ((current || 0) / max) * 100;
  const tw = ((tenx || 0) / max) * 100;
  return (
    <div className="space-y-3" data-testid="cost-bars">
      <div>
        <div className="flex justify-between text-xs text-[#56534A] mb-1">
          <span>Current scale</span>
          <span className="text-[#1A1814] font-medium">${current?.toLocaleString?.() ?? current}/mo</span>
        </div>
        <div className="h-2 bg-[#F0EBE0] rounded-full overflow-hidden">
          <div className="h-full bg-[#C4521E]" style={{ width: `${cw}%` }} />
        </div>
      </div>
      <div>
        <div className="flex justify-between text-xs text-[#56534A] mb-1">
          <span>10× scale</span>
          <span className="text-[#1A1814] font-medium">${tenx?.toLocaleString?.() ?? tenx}/mo</span>
        </div>
        <div className="h-2 bg-[#F0EBE0] rounded-full overflow-hidden">
          <div className="h-full bg-[#C4521E]/60" style={{ width: `${tw}%` }} />
        </div>
      </div>
    </div>
  );
};

const CostBody = ({ data, pricing, computed }) => {
  const [showMath, setShowMath] = useState(false);
  const dom = computed?.dominant_cost;
  const cache = computed?.caching;
  const review = computed?.review;
  return (
    <div className="pt-2 space-y-5">
      {/* THE HEADLINE INSIGHT — what dominates operating cost */}
      {dom?.sentence && (
        <div
          className="border-l-2 border-[#C4521E] bg-[#FBE4D8]/30 px-4 py-3 rounded-r"
          data-testid="dominant-cost"
        >
          <div className="text-[10px] uppercase tracking-[0.16em] text-[#C4521E] mb-1">
            What it really costs
          </div>
          <p className="text-sm text-[#1A1814] leading-relaxed">{dom.sentence}</p>
        </div>
      )}

      {/* Caching split — explicit without-vs-with-cache row */}
      {cache && cache.without_cache_usd > 0 && (
        <div
          className="grid grid-cols-3 gap-3 border border-[#DDD9CF] rounded-md overflow-hidden"
          data-testid="caching-split"
        >
          <div className="p-3 border-r border-[#DDD9CF]">
            <div className="text-[10px] uppercase tracking-[0.14em] text-[#56534A]">Without caching</div>
            <div className="text-base text-[#1A1814] font-medium mt-1 tabular-nums">
              ${cache.without_cache_usd.toLocaleString()}/mo
            </div>
          </div>
          <div className="p-3 border-r border-[#DDD9CF] bg-[#FBF1DC]/40">
            <div className="text-[10px] uppercase tracking-[0.14em] text-[#7A5A12]">
              With {Math.round((cache.cache_hit_rate || 0) * 100)}% cache
            </div>
            <div className="text-base text-[#7A5A12] font-medium mt-1 tabular-nums">
              ${cache.with_cache_usd.toLocaleString()}/mo
            </div>
          </div>
          <div className="p-3">
            <div className="text-[10px] uppercase tracking-[0.14em] text-[#56534A]">Saved by caching</div>
            <div className="text-base text-[#C4521E] font-medium mt-1 tabular-nums">
              ${cache.saving_usd.toLocaleString()}/mo
            </div>
          </div>
        </div>
      )}

      {/* Sanity sentence — always visible — calls out the inputs the calculation came from */}
      {data.sanity_sentence && (
        <p className="text-sm text-[#1A1814] leading-relaxed" data-testid="cost-sanity">
          {data.sanity_sentence}
        </p>
      )}

      {/* Human review cost — the often-dominant hidden number */}
      {review && review.monthly_review_usd > 0 && (
        <div className="border border-[#DDD9CF] rounded-md p-3" data-testid="review-cost">
          <div className="text-[10px] uppercase tracking-[0.14em] text-[#56534A] mb-1">
            Human review cost
          </div>
          <div className="text-sm text-[#1A1814] leading-relaxed">{review.sentence}</div>
          <div className="font-mono text-[11px] text-[#56534A] mt-2 leading-relaxed">
            {review.math_chain}
          </div>
        </div>
      )}

      {pricing && (
        <div
          className="text-[10px] uppercase tracking-[0.14em] text-[#56534A] border border-[#DDD9CF] rounded-md px-3 py-2 flex flex-wrap gap-x-4 gap-y-1"
          data-testid="pricing-stamp"
        >
          <span>
            <span className="text-[#1A1814] normal-case tracking-normal">
              ${pricing.input_per_million_usd}/M in · ${pricing.output_per_million_usd}/M out
            </span>
          </span>
          <span>
            {pricing.source === "litellm"
              ? `Pricing fetched ${(pricing.fetched_at || "").slice(0, 10)}`
              : `Pricing last verified ${pricing.last_verified}`}
          </span>
        </div>
      )}

      <button
        type="button"
        onClick={() => setShowMath((s) => !s)}
        className="text-xs text-[#C4521E] hover:underline flex items-center gap-1"
        data-testid="show-math-toggle"
      >
        {showMath ? "Hide the math" : "Show the math →"}
      </button>

      {showMath && (
        <>
          {Array.isArray(data.breakdown) && data.breakdown.length > 0 && (
            <div data-testid="cost-breakdown">
              <div className="text-xs uppercase tracking-[0.14em] text-[#56534A] mb-2">Step breakdown</div>
              <div className="border border-[#DDD9CF] rounded-md overflow-hidden">
                {data.breakdown.map((b, i) => {
                  const t = TYPE_COLORS[b.type] || TYPE_COLORS.Free;
                  return (
                    <div
                      key={i}
                      className={`grid grid-cols-[1fr_90px_100px] items-center px-3 py-2 text-sm ${
                        i < data.breakdown.length - 1 ? "border-b border-[#DDD9CF]" : ""
                      }`}
                    >
                      <div className="text-[#1A1814] truncate">{b.step}</div>
                      <div className="flex items-center gap-2" style={{ color: t.text }}>
                        <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: t.dot }} />
                        <span className="text-xs">{b.type}</span>
                      </div>
                      <div className="text-right text-xs text-[#56534A]">
                        {b.type === "Free" ? "—" : b.cost_per_call}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Full per-step math chain — the literal arithmetic so a PM can verify any cent */}
          {Array.isArray(data.math_chains) && data.math_chains.length > 0 && (
            <div data-testid="cost-math-chains">
              <div className="text-xs uppercase tracking-[0.14em] text-[#56534A] mb-2">Calculation</div>
              <div className="bg-[#F9F7F3] border border-[#DDD9CF] rounded-md p-3 space-y-1.5 font-mono text-[11px] text-[#1A1814] leading-relaxed">
                {data.math_chains.map((line, i) => (
                  <div key={i}>{line}</div>
                ))}
                {review?.math_chain && review.monthly_review_usd > 0 && (
                  <div className="pt-1.5 mt-1.5 border-t border-[#DDD9CF]">
                    Review · {review.math_chain}
                  </div>
                )}
                {cache?.math_chain && cache.without_cache_usd > 0 && (
                  <div>Caching · {cache.math_chain}</div>
                )}
              </div>
            </div>
          )}

          {(typeof data.monthly_current_usd === "number" || typeof data.monthly_10x_usd === "number") && (
            <div>
              <div className="text-xs uppercase tracking-[0.14em] text-[#56534A] mb-2">Monthly totals</div>
              <CostBars current={data.monthly_current_usd} tenx={data.monthly_10x_usd} />
            </div>
          )}

          {data.model_recommendation && (
            <div>
              <div className="text-xs uppercase tracking-[0.14em] text-[#56534A] mb-1">Model recommendation</div>
              <p className="text-sm text-[#1A1814] leading-relaxed">{data.model_recommendation}</p>
            </div>
          )}

          {data.build_vs_rules && (
            <div>
              <div className="text-xs uppercase tracking-[0.14em] text-[#56534A] mb-1">Build vs rules</div>
              <p className="text-sm text-[#1A1814] leading-relaxed">{data.build_vs_rules}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const RiskBody = ({ data, computed }) => {
  const floor = computed?.value_floor;
  const error = computed?.error_exposure;
  return (
    <div className="pt-2 space-y-5">
      {/* THE DECISION NUMBER — the monthly value floor this feature must clear */}
      {floor?.sentence && (
        <div
          className="border-l-2 border-[#C4521E] bg-[#FBE4D8]/30 px-4 py-3 rounded-r"
          data-testid="value-floor"
        >
          <div className="text-[10px] uppercase tracking-[0.16em] text-[#C4521E] mb-1">
            The bar this must clear
          </div>
          <p className="text-sm text-[#1A1814] leading-relaxed">{floor.sentence}</p>
          <div className="font-mono text-[11px] text-[#56534A] mt-2 leading-relaxed">
            {floor.math_chain}
          </div>
        </div>
      )}

      {/* Wrong-output exposure as its own deterministic line */}
      {error?.sentence && (
        <div className="text-sm text-[#1A1814] leading-relaxed" data-testid="error-exposure">
          <span className="uppercase tracking-[0.14em] text-[10px] text-[#56534A] mr-2">
            Wrong-output exposure
          </span>
          <span>{error.sentence}</span>
        </div>
      )}

      {(data.risks || []).map((r, i) => (
        <div key={i} className="border-l-2 border-[#C4521E]/40 pl-4" data-testid={`risk-${i}`}>
          <div className="flex items-center justify-between gap-3">
            <h4 className="font-serif-display text-base text-[#1A1814]">{r.name}</h4>
            <Pill tone={r.likelihood}>{r.likelihood}</Pill>
          </div>
          <p className="mt-2 text-sm text-[#1A1814] leading-relaxed">{r.scenario}</p>
          <p className="mt-1 text-xs text-[#56534A] italic">Why: {r.likelihood_reason}</p>
          {r.cost_if_materialises && (
            <div
              className="mt-3 border-l-2 border-[#C4521E] bg-[#FBE4D8]/30 px-3 py-2 rounded-r"
              data-testid={`risk-${i}-cost`}
            >
              <div className="text-[10px] uppercase tracking-[0.14em] text-[#C4521E] mb-1">
                If it fails
              </div>
              <p className="text-xs text-[#1A1814] leading-relaxed">{r.cost_if_materialises}</p>
            </div>
          )}
          <p className="mt-2 text-xs">
            <span className="uppercase tracking-[0.14em] text-[#56534A] mr-2">Mitigation</span>
            <span className="text-[#1A1814]">{r.mitigation}</span>
          </p>
        </div>
      ))}
    </div>
  );
};

const RippleBody = ({ data }) => (
  <div className="pt-2">
    {data.door_opened && (
      <p className="text-[#1A1814] font-serif-display italic text-base leading-relaxed">
        {data.door_opened}
      </p>
    )}
    <div className="mt-4 space-y-4">
      {(data.opportunities || []).map((o, i) => (
        <div key={i} className="border-b border-[#DDD9CF] pb-3 last:border-b-0" data-testid={`opportunity-${i}`}>
          <h4 className="font-serif-display text-sm text-[#C4521E]">
            <span className="text-[#56534A] mr-2">{String(i + 1).padStart(2, "0")}</span>
            {o.title}
          </h4>
          <p className="text-sm text-[#1A1814] mt-1 leading-relaxed">{o.description}</p>
        </div>
      ))}
    </div>
  </div>
);

export const Phase3Output = ({
  analysis,
  modelLabel,
  onReanalyze,
  busy,
  followupValue,
  setFollowupValue,
  onFollowupSubmit,
  followupBusy,
  followupListening,
  onFollowupMicToggle,
  followupSupported,
}) => {
  if (!analysis) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="mt-10"
      data-testid="phase-3-output"
    >
      <div className="hr-ink mb-6" />

      <div className="flex items-start justify-between gap-4 mb-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#56534A] mb-2">The full picture</div>
          <h2 className="font-serif-display text-3xl md:text-4xl text-[#1A1814]">Here it is.</h2>
        </div>
        <button
          type="button"
          onClick={onReanalyze}
          disabled={busy}
          className="text-sm text-[#56534A] hover:text-[#C4521E] transition-colors flex items-center gap-1.5 disabled:opacity-40 whitespace-nowrap mt-2"
          data-testid="reanalyze-button"
          title={`Re-run analysis with ${modelLabel}`}
        >
          <RefreshCw size={13} strokeWidth={1.5} className={busy ? "animate-spin" : ""} />
          {busy ? "Re-analysing…" : "Re-analyse"}
        </button>
      </div>

      {/* Feature flow */}
      {analysis.feature_flow?.length > 0 && <FlowDiagram steps={analysis.feature_flow} />}

      {/* Decision banner */}
      {analysis.decision && (
        <div
          className="mt-8 border border-[#C4521E] rounded-md p-5 bg-[#FBE4D8]/40 flex gap-5"
          data-testid="decision-banner"
        >
          <div className="text-xs uppercase tracking-[0.18em] text-[#C4521E] font-medium shrink-0 mt-1">
            Decision
          </div>
          <div className="text-[#1A1814] leading-relaxed">
            {analysis.decision.headline && (
              <span className="font-medium">{analysis.decision.headline} </span>
            )}
            <span>{analysis.decision.body}</span>
          </div>
        </div>
      )}

      {/* What matters here — unmissable strip with the two deterministic sentences */}
      <WhatMattersStrip
        decision={analysis.decision}
        computed={analysis.computed}
        modelLabel={modelLabel}
      />

      {/* Ethical flags + data quality strip */}
      {((analysis.ethical_flags && analysis.ethical_flags.length > 0) ||
        (analysis.data_quality && analysis.data_quality.level && analysis.data_quality.level !== "unknown")) && (
        <div className="mt-4 flex flex-wrap items-start gap-3" data-testid="flags-strip">
          {analysis.data_quality?.level && analysis.data_quality.level !== "unknown" && (
            <div
              className="border border-[#DDD9CF] bg-white rounded-md px-3 py-2 text-xs flex items-start gap-2"
              data-testid="data-quality-callout"
              title={analysis.data_quality.note}
            >
              <span
                className="inline-block w-2 h-2 rounded-full mt-1.5 shrink-0"
                style={{
                  backgroundColor:
                    analysis.data_quality.level === "validated"
                      ? "#5A8A3A"
                      : analysis.data_quality.level === "mostly_clean"
                      ? "#E8A930"
                      : "#C4521E",
                }}
              />
              <span>
                <span className="uppercase tracking-[0.14em] text-[#56534A] mr-1">Data quality</span>
                <span className="text-[#1A1814] capitalize">
                  {analysis.data_quality.level.replace("_", " ")}
                </span>
                {analysis.data_quality.note && (
                  <span className="text-[#56534A] block mt-0.5 leading-relaxed normal-case">
                    {analysis.data_quality.note}
                  </span>
                )}
              </span>
            </div>
          )}
          {(analysis.ethical_flags || []).map((f, i) => (
            <div
              key={i}
              className="border border-[#C4521E]/60 bg-[#FBE4D8]/30 rounded-md px-3 py-2 text-xs"
              data-testid={`ethical-flag-${i}`}
              title={f.reason}
            >
              <span className="uppercase tracking-[0.14em] text-[#C4521E] mr-1">⚐ Ethical</span>
              <span className="text-[#1A1814]">{f.label}</span>
              {f.reason && (
                <span className="text-[#56534A] block mt-0.5 leading-relaxed">{f.reason}</span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* 2x2 grid on tablet/laptop, 4x1 on ultrawide (≥1536px) */}
      <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-4 gap-4 mt-8">
        <Card
          title="Value"
          headline={analysis.value?.headline}
          defaultOpen
          testid="card-value"
        >
          <ValueBody data={analysis.value || {}} />
        </Card>
        <Card title="Cost" headline={analysis.cost?.headline} testid="card-cost">
          <CostBody data={analysis.cost || {}} pricing={analysis.pricing} computed={analysis.computed} />
        </Card>
        <Card title="Risk" headline={analysis.risk?.headline} testid="card-risk">
          <RiskBody data={analysis.risk || {}} computed={analysis.computed} />
        </Card>
        <Card title="The ripple" headline={analysis.ripple?.headline} testid="card-ripple">
          <RippleBody data={analysis.ripple || {}} />
        </Card>
      </div>

      {/* Follow-up */}
      <div className="mt-8 border border-[#DDD9CF] rounded-md p-5 bg-white" data-testid="followup-block">
        <p className="font-serif-display italic text-[#1A1814] text-base">
          "What assumption in this analysis are you least confident about?"
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (followupValue.trim() && !followupBusy) onFollowupSubmit();
          }}
          className="mt-3 flex items-center gap-3"
        >
          <input
            type="text"
            value={followupValue}
            onChange={(e) => setFollowupValue(e.target.value)}
            placeholder="Type your answer or speak it…"
            disabled={followupBusy}
            className="flex-1 bg-transparent border border-[#DDD9CF] focus:border-[#C4521E] rounded px-3 py-2 text-sm text-[#1A1814] placeholder-[#56534A]/70 outline-none transition-colors"
            data-testid="followup-input"
          />
          {followupSupported && (
            <button
              type="button"
              onClick={onFollowupMicToggle}
              className={`w-10 h-10 rounded-full border flex items-center justify-center transition-colors shrink-0 ${
                followupListening
                  ? "bg-[#C4521E] border-[#C4521E] text-[#F9F7F3] mic-listening"
                  : "border-[#DDD9CF] text-[#56534A] hover:text-[#C4521E]"
              }`}
              data-testid="followup-mic"
              aria-label={followupListening ? "Stop recording" : "Speak follow-up"}
            >
              <Mic size={16} strokeWidth={1.5} />
            </button>
          )}
          <button
            type="submit"
            disabled={!followupValue.trim() || followupBusy}
            className="border border-[#DDD9CF] hover:border-[#C4521E] hover:text-[#C4521E] text-[#1A1814] px-4 py-2 text-sm transition-colors disabled:opacity-40 shrink-0 rounded"
            data-testid="followup-submit"
          >
            {followupBusy ? "…" : "Challenge it"}
          </button>
        </form>
      </div>
    </motion.div>
  );
};
